import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Card } from '../components/ui/card';
import { MapPin, Building, Globe, Zap } from 'lucide-react';
import { globalLocations } from '../mock';

const GlobalPresence = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Global Presence | Eaglesway Group - International Operations</title>
        <meta 
          name="description" 
          content="Eaglesway Group operates across 4 continents with offices in UAE, UK, USA, and South Korea representative office. Discover our strategic global network and international trading capabilities." 
        />
      </Helmet>

      <div className="min-h-screen bg-black text-white pt-20">
        {/* Hero Section with World Map Network Visualization */}
        <section className="relative h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            {/* World Map Background */}
            <img
              src="https://images.unsplash.com/photo-1570106413982-7f2897b8d0c5?q=80&w=2000"
              alt="Global network"
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black"></div>

            {/* SVG Network Connection Lines - UAE, UK, USA, South Korea */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="xMidYMid slice">
              {/* UAE to UK Connection Line */}
              <line
                x1="1200" y1="600"
                x2="800" y2="400"
                stroke="url(#goldGradient)"
                strokeWidth="2"
                className="animate-pulse-line"
                strokeDasharray="5,5"
              />
              
              {/* UK to USA Connection Line */}
              <line
                x1="800" y1="400"
                x2="400" y2="500"
                stroke="url(#goldGradient)"
                strokeWidth="2"
                className="animate-pulse-line"
                strokeDasharray="5,5"
                style={{ animationDelay: '0.5s' }}
              />
              
              {/* UAE to USA Connection Line */}
              <line
                x1="1200" y1="600"
                x2="400" y2="500"
                stroke="url(#goldGradient)"
                strokeWidth="2"
                className="animate-pulse-line"
                strokeDasharray="5,5"
                style={{ animationDelay: '1s' }}
              />

              {/* UAE to South Korea Connection Line */}
              <line
                x1="1200" y1="600"
                x2="1400" y2="450"
                stroke="url(#goldGradient)"
                strokeWidth="2"
                className="animate-pulse-line"
                strokeDasharray="5,5"
                style={{ animationDelay: '1.5s' }}
              />

              {/* Gradient Definition */}
              <defs>
                <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" style={{ stopColor: '#D4AF37', stopOpacity: 0.3 }} />
                  <stop offset="50%" style={{ stopColor: '#FFD700', stopOpacity: 0.8 }} />
                  <stop offset="100%" style={{ stopColor: '#D4AF37', stopOpacity: 0.3 }} />
                </linearGradient>
              </defs>

              {/* Location Markers with Glow */}
              {/* UAE - Ras Al Khaimah/Dubai */}
              <circle cx="1200" cy="600" r="12" fill="#D4AF37" className="animate-pulse">
                <animate attributeName="r" values="12;16;12" dur="2s" repeatCount="indefinite" />
              </circle>
              <circle cx="1200" cy="600" r="20" fill="none" stroke="#D4AF37" strokeWidth="2" opacity="0.4" />
              
              {/* UK - London */}
              <circle cx="800" cy="400" r="12" fill="#D4AF37" className="animate-pulse">
                <animate attributeName="r" values="12;16;12" dur="2s" repeatCount="indefinite" begin="0.3s" />
              </circle>
              <circle cx="800" cy="400" r="20" fill="none" stroke="#D4AF37" strokeWidth="2" opacity="0.4" />
              
              {/* USA - Delaware */}
              <circle cx="400" cy="500" r="12" fill="#D4AF37" className="animate-pulse">
                <animate attributeName="r" values="12;16;12" dur="2s" repeatCount="indefinite" begin="0.6s" />
              </circle>
              <circle cx="400" cy="500" r="20" fill="none" stroke="#D4AF37" strokeWidth="2" opacity="0.4" />

              {/* South Korea - Cheonan */}
              <circle cx="1400" cy="450" r="12" fill="#D4AF37" className="animate-pulse">
                <animate attributeName="r" values="12;16;12" dur="2s" repeatCount="indefinite" begin="0.9s" />
              </circle>
              <circle cx="1400" cy="450" r="20" fill="none" stroke="#D4AF37" strokeWidth="2" opacity="0.4" />
            </svg>
          </div>

          <div className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="mb-6 inline-flex items-center px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full">
              <Globe className="w-4 h-4 text-[#D4AF37] mr-2" />
              <span className="text-[#D4AF37] text-sm font-medium">Operating Across 4 Continents</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">Global</span>
              <span className="text-[#D4AF37]"> Presence</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 font-light max-w-3xl mx-auto leading-relaxed">
              Strategic locations connecting international markets
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-black/40 backdrop-blur-sm border border-[#D4AF37]/20 rounded-lg p-6">
                <div className="text-4xl font-bold text-[#D4AF37] mb-2">4</div>
                <div className="text-gray-300 text-sm">Continents</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-[#D4AF37]/20 rounded-lg p-6">
                <div className="text-4xl font-bold text-[#D4AF37] mb-2">5</div>
                <div className="text-gray-300 text-sm">Office Locations</div>
              </div>
              <div className="bg-black/40 backdrop-blur-sm border border-[#D4AF37]/20 rounded-lg p-6">
                <div className="text-4xl font-bold text-[#D4AF37] mb-2">24/7</div>
                <div className="text-gray-300 text-sm">Global Support</div>
              </div>
            </div>
          </div>
        </section>

        {/* Office Locations Cards */}
        <section className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #D4AF37 1px, transparent 1px)', backgroundSize: '40px 40px'}}></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Our <span className="text-[#D4AF37]">Locations</span>
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Strategically positioned offices to serve global markets efficiently
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {globalLocations.map((location, index) => (
                <Card
                  key={location.id}
                  className={`group relative bg-gradient-to-br from-gray-900 to-black border-2 border-gray-800 hover:border-[#D4AF37] transition-all duration-500 overflow-hidden transform hover:-translate-y-2 ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000"></div>
                  
                  <div className="p-8 relative z-10">
                    {/* Flag Icon */}
                    <div className="text-6xl mb-6">{location.flag}</div>

                    {/* Location Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center text-[#D4AF37] mb-2">
                          <MapPin size={20} className="mr-2" />
                          <h3 className="text-2xl font-bold">{location.city}</h3>
                        </div>
                        <p className="text-gray-500 text-sm">{location.country}</p>
                      </div>
                      <Zap className="w-6 h-6 text-[#D4AF37] opacity-50" />
                    </div>

                    {/* Office Type */}
                    <div className="inline-block bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] text-xs font-semibold px-4 py-2 rounded-full mb-6">
                      <Building size={14} className="inline mr-2" />
                      {location.type}
                    </div>

                    {/* Representative Info (if applicable) */}
                    {location.representative && (
                      <div className="mb-4 p-4 bg-gray-800/50 border border-gray-700 rounded-lg">
                        <p className="text-[#D4AF37] text-sm font-semibold mb-1">{location.representativeName}</p>
                        <p className="text-gray-400 text-xs">Operated by {location.operatedBy}</p>
                        <p className="text-gray-300 text-xs mt-2">Contact: {location.contactPerson}</p>
                      </div>
                    )}

                    {/* Address & Contact */}
                    <div className="space-y-3 pt-4 border-t border-gray-800">
                      <div>
                        <p className="text-gray-500 text-xs uppercase mb-1">Address</p>
                        <p className="text-gray-300 text-sm">{location.address}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs uppercase mb-1">Contact</p>
                        <a 
                          href={`tel:${location.phone}`}
                          className="text-gray-300 text-sm font-medium hover:text-[#D4AF37] transition-colors"
                        >
                          {location.phone}
                        </a>
                      </div>
                      {location.email && (
                        <div>
                          <p className="text-gray-500 text-xs uppercase mb-1">Email</p>
                          <a 
                            href={`mailto:${location.email}`}
                            className="text-gray-300 text-sm font-medium hover:text-[#D4AF37] transition-colors"
                          >
                            {location.email}
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Glow Effect on Hover */}
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" 
                         style={{boxShadow: '0 0 40px rgba(212, 175, 55, 0.2)'}}></div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Global Network Benefits */}
        <section className="py-24 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Why Our <span className="text-[#D4AF37]">Global Network</span> Matters
              </h2>
              <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                Strategic positioning for seamless international operations
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 rounded-xl p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Rapid Response</h3>
                <p className="text-gray-400">
                  Local presence enables faster decision-making and immediate support across time zones
                </p>
              </div>

              <div className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 rounded-xl p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Globe className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Market Intelligence</h3>
                <p className="text-gray-400">
                  Deep understanding of regional markets, regulations, and business cultures
                </p>
              </div>

              <div className="bg-gradient-to-b from-gray-900 to-black border border-gray-800 rounded-xl p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">Seamless Logistics</h3>
                <p className="text-gray-400">
                  Coordinated operations across continents for efficient supply chain management
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GlobalPresence;
